import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Loader2, CheckCircle2, ExternalLink, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import ProposalCardShell from './ProposalCardShell';
import { routinesApi } from '@/services/routines-api';
import { handleApiError } from '@/services/api';
import { humanReadableCron } from '@/utils/cron';
import type { ToolSegmentPayload } from '@/types/chat';

type Proposal = Extract<ToolSegmentPayload, { type: 'routine_update_proposal' }>;

interface Props {
  proposal: Proposal;
}

interface DiffRow {
  label: string;
  before: string;
  after: string;
  changed: boolean;
}

const RoutineUpdateProposalCard: React.FC<Props> = ({ proposal }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [applied, setApplied] = useState(false);

  const current = proposal.current as {
    name?: string;
    schedule?: string;
    timezone?: string;
    message?: string;
  };

  const rows: DiffRow[] = [
    {
      label: 'Name',
      before: current.name ?? '(unknown)',
      after: proposal.proposed.name,
      changed: (current.name ?? '') !== proposal.proposed.name,
    },
    {
      label: 'Schedule',
      before: current.schedule
        ? `${humanReadableCron(current.schedule)} (${current.schedule})`
        : '(unknown)',
      after:
        proposal.proposed.schedule.human_readable !== '(unchanged)'
          ? `${proposal.proposed.schedule.human_readable} (${proposal.proposed.schedule.expression})`
          : `${humanReadableCron(proposal.proposed.schedule.expression)} (${proposal.proposed.schedule.expression})`,
      changed: (current.schedule ?? '') !== proposal.proposed.schedule.expression,
    },
    {
      label: 'Timezone',
      before: current.timezone ?? '(unknown)',
      after: proposal.proposed.timezone ?? current.timezone ?? '(unchanged)',
      changed: (current.timezone ?? '') !== (proposal.proposed.timezone ?? current.timezone ?? ''),
    },
    {
      label: 'Prompt',
      before: current.message ?? '(unknown)',
      after: proposal.proposed.derived_prompt,
      changed: (current.message ?? '') !== proposal.proposed.derived_prompt,
    },
  ];

  const updateMutation = useMutation({
    mutationFn: () =>
      routinesApi.updateRoutine(proposal.routine_id, {
        ...(rows[0].changed ? { name: proposal.proposed.name } : {}),
        ...(rows[1].changed ? { schedule: proposal.proposed.schedule.expression } : {}),
        ...(rows[2].changed ? { timezone: proposal.proposed.timezone ?? undefined } : {}),
        ...(rows[3].changed ? { message: proposal.proposed.derived_prompt } : {}),
      }),
    onSuccess: () => {
      setApplied(true);
      toast.success('Routine updated');
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update routine');
    },
  });

  if (dismissed) {
    return (
      <ProposalCardShell
        kicker="Routine update"
        title={current.name ?? 'Routine'}
        actions={null}
        accentClass="bg-primary"
        resolved={{ tone: 'muted', label: 'Dismissed' }}
      >
        <div />
      </ProposalCardShell>
    );
  }

  if (applied) {
    return (
      <ProposalCardShell
        kicker="Routine update"
        title={proposal.proposed.name}
        actions={null}
        accentClass="bg-primary"
        resolved={{
          tone: 'success',
          label: '✓ Update applied',
          action: (
            <button
              type="button"
              onClick={() => navigate(`/routines/${proposal.routine_id}`)}
              className="inline-flex items-center gap-1 text-xs font-medium text-success hover:underline"
            >
              View routine
              <ExternalLink className="w-3 h-3" />
            </button>
          ),
        }}
      >
        <div />
      </ProposalCardShell>
    );
  }

  return (
    <ProposalCardShell
      kicker="Routine update"
      title={current.name ?? 'Routine'}
      subtitle={proposal.rationale}
      accentClass="bg-primary"
      actions={
        <>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            disabled={updateMutation.isPending}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={() => navigate(`/routines/${proposal.routine_id}/edit`)}
            disabled={updateMutation.isPending}
            className="px-3 py-1.5 rounded-lg text-sm font-medium border border-input dark:border-white/10 text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
          >
            Edit further
          </button>
          <button
            type="button"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending || !rows.some((r) => r.changed)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updateMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5" />
            )}
            Apply update
          </button>
        </>
      }
    >
      <div className="rounded-lg border border-border divide-y divide-border">
        {rows.map((r) => (
          <div
            key={r.label}
            className={`p-3 text-sm ${
              r.changed ? 'bg-warn/15' : 'bg-muted dark:bg-white/[0.02]'
            }`}
          >
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {r.label}
              {!r.changed && (
                <span className="ml-2 text-[10px] text-muted-foreground normal-case tracking-normal">
                  (unchanged)
                </span>
              )}
            </div>
            {r.changed ? (
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-start gap-2">
                <div className="text-muted-foreground line-through whitespace-pre-wrap break-words">
                  {r.before}
                </div>
                <ArrowRight className="w-4 h-4 text-warn mt-0.5 hidden sm:block" />
                <div className="text-foreground whitespace-pre-wrap break-words">
                  {r.after}
                </div>
              </div>
            ) : (
              <div className="text-foreground whitespace-pre-wrap break-words">
                {r.before}
              </div>
            )}
          </div>
        ))}
      </div>
    </ProposalCardShell>
  );
};

export default RoutineUpdateProposalCard;
